// pages_notify.js — Notification Recipients Management (SuperAdmin only)
import React, { useState, useEffect } from 'react';
import { Btn, Inp, Sel, Modal, PageHeader, Panel, KPI } from './utils';

const T = {
  accent:"#C48A0E", gold:"#C48A0E", text:"#1A2235", muted:"#8492A6",
  border:"#E2E8F0", faint:"rgba(26,34,53,0.04)", blue:"#3B82F6",
  green:"#22C55E", red:"#EF4444", orange:"#F97316", purple:"#7C3AED",
  font:"'DM Sans','Segoe UI',system-ui,sans-serif",
};

const API = (typeof process !== 'undefined' && process.env?.REACT_APP_API_URL)
  ? process.env.REACT_APP_API_URL : '';

const apiFetch = async (path) => {
  const token = localStorage.getItem('auravex_token');
  const res = await fetch(API + path, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
};

const apiCall = async (method, path, body) => {
  const token = localStorage.getItem('auravex_token');
  const res = await fetch(API + path, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: body ? JSON.stringify(body) : undefined,
  });
  return res.json();
};

const NOTIFY_TYPE_META = {
  tenant_signup:  { label: 'New Tenant Signup',       icon: '🎉', color: '#16a34a' },
  system_alert:   { label: 'System Alerts',            icon: '🔴', color: '#dc2626' },
  subscription:   { label: 'Subscription / Plan Change',icon: '📦', color: '#2563eb' },
  user_added:     { label: 'New User Added',            icon: '👤', color: '#7c3aed' },
  model_request:  { label: 'AI Model Requests',        icon: '🤖', color: '#d97706' },
};

const ALL_TYPES = Object.keys(NOTIFY_TYPE_META);

/* ── Notification Type Toggle ── */
const TypeToggle = ({ types, onChange }) => {
  const active = (types || '').split(',').map(t=>t.trim()).filter(Boolean);
  const toggle = (type) => {
    const next = active.includes(type)
      ? active.filter(t=>t!==type)
      : [...active, type];
    onChange(next.join(','));
  };
  return (
    <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginTop:4 }}>
      {ALL_TYPES.map(type => {
        const meta = NOTIFY_TYPE_META[type];
        const on   = active.includes(type);
        return (
          <button key={type} onClick={()=>toggle(type)}
            style={{
              display:'flex', alignItems:'center', gap:5,
              padding:'4px 10px', borderRadius:20, cursor:'pointer',
              border:`1px solid ${on ? meta.color : T.border}`,
              background: on ? `${meta.color}14` : 'transparent',
              color: on ? meta.color : T.muted,
              fontSize:11, fontWeight:700, transition:'all .15s',
            }}>
            <span>{meta.icon}</span> {meta.label}
          </button>
        );
      })}
    </div>
  );
};

/* ── Main Page ── */
export const PageNotifyRecipients = ({ toast }) => {
  const [recipients, setRecipients] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [showForm,   setShowForm]   = useState(false);
  const [editRec,    setEditRec]    = useState(null);
  const [testing,    setTesting]    = useState(null); // id being tested
  const [form, setForm] = useState({
    label:'', email:'', mobile:'', active:true,
    notify_types: ALL_TYPES.join(','),
  });

  const load = () => {
    setLoading(true);
    apiFetch('/api/alert-recipients')
      .then(setRecipients)
      .catch(e => toast(e.message, 'warn'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const openAdd = () => {
    setEditRec(null);
    setForm({ label:'', email:'', mobile:'', active:true, notify_types: ALL_TYPES.join(',') });
    setShowForm(true);
  };

  const openEdit = (r) => {
    setEditRec(r);
    setForm({ label:r.label, email:r.email||'', mobile:r.mobile||'', active:r.active, notify_types:r.notify_types||'' });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.label) { toast('Label is required', 'warn'); return; }
    if (!form.email && !form.mobile) { toast('Enter at least email or mobile', 'warn'); return; }
    try {
      let res;
      if (editRec) {
        res = await apiCall('PUT', `/api/alert-recipients/${editRec.id}`, form);
      } else {
        res = await apiCall('POST', '/api/alert-recipients', form);
      }
      if (res.error) { toast(res.error, 'warn'); return; }
      toast(`✅ Recipient ${editRec ? 'updated' : 'added'} successfully`, 'success');
      setShowForm(false);
      load();
    } catch(e) { toast(e.message, 'warn'); }
  };

  const remove = async (id, label) => {
    if (!window.confirm(`Remove "${label}" from notification recipients?`)) return;
    const res = await apiCall('DELETE', `/api/alert-recipients/${id}`);
    if (res.error) { toast(res.error, 'warn'); return; }
    toast('Recipient removed', 'warn');
    load();
  };

  const testNotify = async (id) => {
    setTesting(id);
    try {
      const res = await apiCall('POST', '/api/alert-recipients/test', { recipientId: id });
      if (res.error) { toast(res.error, 'warn'); return; }
      const r = res.results || {};
      const msgs = [];
      if (r.email?.success) msgs.push(r.email.dev ? '📧 Email logged (SMTP not configured)' : `📧 Email sent (${r.email.messageId?.slice(0,20)||'OK'})`);
      if (r.email?.error)   msgs.push(`📧 Email FAILED: ${r.email.error}`);
      if (r.sms?.success)   msgs.push(r.sms.dev ? '📱 SMS logged (dev mode)' : '📱 SMS sent');
      if (r.sms?.error)     msgs.push(`📱 SMS FAILED: ${r.sms.error}`);
      toast(msgs.join(' · ') || '✅ Test sent', msgs.some(m=>m.includes('FAIL')) ? 'warn' : 'success');
    } catch(e) { toast('Test failed: ' + e.message, 'warn'); }
    finally { setTesting(null); }
  };

  const toggleActive = async (r) => {
    const res = await apiCall('PUT', `/api/alert-recipients/${r.id}`, { ...r, active: !r.active });
    if (res.error) { toast(res.error, 'warn'); return; }
    load();
  };

  const activeCount  = recipients.filter(r=>r.active).length;
  const emailCount   = recipients.filter(r=>r.email).length;
  const mobileCount  = recipients.filter(r=>r.mobile).length;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      <PageHeader
        title="🔔 Notification Recipients"
        sub="Manage who gets notified for platform events — new signups, AI model requests, subscription changes, system alerts">
        <Btn onClick={openAdd}>+ Add Recipient</Btn>
      </PageHeader>

      {/* KPIs */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10 }}>
        <KPI label="Total Recipients" value={recipients.length}  icon="👥" color={T.blue}/>
        <KPI label="Active"           value={activeCount}        icon="✅" color={T.green}/>
        <KPI label="Email"            value={emailCount}         icon="📧" color={T.purple}/>
        <KPI label="SMS"              value={mobileCount}        icon="📱" color={T.orange}/>
      </div>

      {/* SMTP status banner */}
      <div style={{
        padding:'12px 16px', borderRadius:10,
        background: 'rgba(59,130,246,0.06)', border:'1px solid rgba(59,130,246,0.2)',
        display:'flex', gap:12, alignItems:'flex-start',
      }}>
        <span style={{ fontSize:20 }}>⚙️</span>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:12, fontWeight:700, color:T.text, marginBottom:4 }}>Gmail SMTP Setup</div>
          <div style={{ fontSize:11, color:T.muted, lineHeight:1.6 }}>
            To enable real email delivery, set <code style={{ background:'#f1f5f9', padding:'1px 5px', borderRadius:3, color:T.text }}>SMTP_PASS</code> in <code style={{ background:'#f1f5f9', padding:'1px 5px', borderRadius:3 }}>docker-compose.dev.yml</code>.
            Generate a Gmail App Password: <strong>Google Account → Security → 2-Step Verification → App Passwords</strong>.
            Until configured, notifications are logged to console only. Use the <strong>Test</strong> button to verify.
          </div>
        </div>
      </div>

      {/* Event types legend */}
      <div style={{ background:'#fff', borderRadius:10, border:`1px solid ${T.border}`, padding:'14px 16px' }}>
        <div style={{ fontSize:11, fontWeight:800, color:T.muted, letterSpacing:1, marginBottom:10 }}>NOTIFICATION EVENT TYPES</div>
        <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
          {ALL_TYPES.map(type => {
            const meta = NOTIFY_TYPE_META[type];
            const count = recipients.filter(r => r.active && (r.notify_types||'').includes(type)).length;
            return (
              <div key={type} style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 12px',
                borderRadius:8, background:`${meta.color}08`, border:`1px solid ${meta.color}22` }}>
                <span style={{ fontSize:14 }}>{meta.icon}</span>
                <div>
                  <div style={{ fontSize:11, fontWeight:700, color:meta.color }}>{meta.label}</div>
                  <div style={{ fontSize:9, color:T.muted }}>{count} active recipient{count!==1?'s':''}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recipients list */}
      <Panel title="RECIPIENTS">
        {loading ? (
          <div style={{ textAlign:'center', padding:40, color:T.muted }}>Loading...</div>
        ) : recipients.length === 0 ? (
          <div style={{ textAlign:'center', padding:40 }}>
            <div style={{ fontSize:36, marginBottom:8 }}>🔕</div>
            <div style={{ fontSize:13, color:T.muted }}>No recipients configured</div>
            <div style={{ marginTop:12 }}><Btn onClick={openAdd}>+ Add First Recipient</Btn></div>
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {recipients.map(r => (
              <div key={r.id} style={{
                background: r.active ? '#fff' : '#fafafa',
                borderRadius:10, border:`1px solid ${r.active ? T.border : '#e5e7eb'}`,
                padding:'14px 16px', display:'flex', gap:12, alignItems:'flex-start',
                opacity: r.active ? 1 : 0.65,
              }}>
                {/* Avatar */}
                <div style={{
                  width:40, height:40, borderRadius:10, flexShrink:0, display:'flex',
                  alignItems:'center', justifyContent:'center', fontSize:18,
                  background: r.active ? 'rgba(34,197,94,0.1)' : '#f3f4f6',
                  border:`1px solid ${r.active ? 'rgba(34,197,94,0.3)' : '#e5e7eb'}`,
                }}>
                  {r.email && r.mobile ? '📧📱' : r.email ? '📧' : '📱'}
                </div>

                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4, flexWrap:'wrap' }}>
                    <span style={{ fontSize:13, fontWeight:800, color:T.text }}>{r.label}</span>
                    <span style={{
                      fontSize:10, padding:'1px 7px', borderRadius:10, fontWeight:700,
                      background: r.active ? 'rgba(34,197,94,0.1)' : '#f3f4f6',
                      color: r.active ? T.green : T.muted,
                    }}>{r.active ? 'Active' : 'Paused'}</span>
                  </div>
                  <div style={{ display:'flex', gap:14, flexWrap:'wrap', marginBottom:8 }}>
                    {r.email  && <span style={{ fontSize:11, color:T.muted }}>📧 {r.email}</span>}
                    {r.mobile && <span style={{ fontSize:11, color:T.muted }}>📱 {r.mobile}</span>}
                  </div>
                  {/* Notify types */}
                  <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                    {(r.notify_types||'').split(',').filter(Boolean).map(type => {
                      const meta = NOTIFY_TYPE_META[type.trim()];
                      return meta ? (
                        <span key={type} style={{
                          fontSize:9, padding:'2px 7px', borderRadius:10, fontWeight:700,
                          background:`${meta.color}12`, color:meta.color,
                        }}>{meta.icon} {meta.label}</span>
                      ) : null;
                    })}
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                  <button onClick={() => testNotify(r.id)} disabled={testing===r.id}
                    style={{
                      padding:'5px 10px', borderRadius:7, border:`1px solid ${T.border}`,
                      background:'none', color:T.blue, fontSize:11, fontWeight:700, cursor:'pointer',
                    }}>
                    {testing===r.id ? '...' : '🧪 Test'}
                  </button>
                  <button onClick={() => toggleActive(r)}
                    style={{
                      padding:'5px 10px', borderRadius:7,
                      border:`1px solid ${r.active?'rgba(239,68,68,0.3)':'rgba(34,197,94,0.3)'}`,
                      background: r.active?'rgba(239,68,68,0.06)':'rgba(34,197,94,0.06)',
                      color: r.active?T.red:T.green, fontSize:11, fontWeight:700, cursor:'pointer',
                    }}>
                    {r.active ? '⏸ Pause' : '▶ Enable'}
                  </button>
                  <button onClick={() => openEdit(r)}
                    style={{
                      padding:'5px 10px', borderRadius:7, border:`1px solid rgba(59,130,246,0.3)`,
                      background:'rgba(59,130,246,0.06)', color:T.blue, fontSize:11, fontWeight:700, cursor:'pointer',
                    }}>
                    ✏️ Edit
                  </button>
                  <button onClick={() => remove(r.id, r.label)}
                    style={{
                      padding:'5px 8px', borderRadius:7, border:`1px solid rgba(239,68,68,0.2)`,
                      background:'none', color:T.red, fontSize:11, cursor:'pointer',
                    }}>
                    🗑
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {/* Add / Edit Modal */}
      <Modal open={showForm} title={editRec ? `✏️ Edit: ${editRec.label}` : '+ Add Notification Recipient'} onClose={()=>setShowForm(false)}>
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          <Inp label="DISPLAY LABEL *" value={form.label} onChange={v=>setForm(p=>({...p,label:v}))}
            placeholder="e.g. Super Admin — Pradeep, Sales Team, Support"/>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Inp label="EMAIL ADDRESS" value={form.email} onChange={v=>setForm(p=>({...p,email:v}))}
              placeholder="gilbile.pradeep@gmail.com"/>
            <Inp label="MOBILE (with country code)" value={form.mobile} onChange={v=>setForm(p=>({...p,mobile:v}))}
              placeholder="+917757997350"/>
          </div>

          <div>
            <div style={{ fontSize:9, color:T.muted, fontWeight:700, letterSpacing:1, marginBottom:6 }}>
              NOTIFY FOR THESE EVENTS (click to toggle)
            </div>
            <TypeToggle types={form.notify_types} onChange={v=>setForm(p=>({...p,notify_types:v}))}/>
          </div>

          <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px',
            background:'rgba(34,197,94,0.06)', border:'1px solid rgba(34,197,94,0.2)', borderRadius:8 }}>
            <input type="checkbox" checked={form.active}
              onChange={e=>setForm(p=>({...p,active:e.target.checked}))}
              id="activeChk" style={{ accentColor:T.green, width:14, height:14 }}/>
            <label htmlFor="activeChk" style={{ fontSize:12, color:T.text, cursor:'pointer', fontWeight:600 }}>
              Active — receive notifications immediately
            </label>
          </div>

          <div style={{ display:'flex', gap:8, justifyContent:'flex-end', paddingTop:4 }}>
            <Btn v="ghost" onClick={()=>setShowForm(false)}>Cancel</Btn>
            <Btn onClick={save}>{editRec ? '💾 Update' : '+ Add Recipient'}</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
};
